import {
  Feather,
  Bookmark,
  Bird,
  Coffee,
  Key,
  Sailboat,
  Moon,
  Lamp,
  type LucideIcon
} from 'lucide-react';

/**
 * Die Symbole der Mitarbeiter-Identität (A9.1).
 *
 * WARUM ES DIESE LISTE GIBT
 *   Auf dem geteilten Tablet am Tresen erfasst leicht jemand einen
 *   Eintrag unter dem Namen der Kollegin, die noch angemeldet ist — und
 *   das Kommunikationsprotokoll ist append-only. Gesucht ist deshalb kein
 *   Namensschild, sondern ein Fremdheitssignal: „das ist mir fremd, was
 *   ist los?"
 *
 *   Die Nutzerfarbe leistet das seit PR #16, ohne dass man hinsieht. Ihre
 *   Grenze ist, dass sie sich nicht aussprechen lässt. Am Tresen sagt
 *   jemand „das war die Tasse"; „das war das gedämpfte Violett" sagt
 *   niemand. Erst mit einem Wort wird aus dem Signal ein Gespräch —
 *   deshalb Motive mit Namen und keine generierten Muster.
 *
 * DIE BEZEICHNER SIND DIE GEMEINSAME SPRACHE
 *   `users.symbol` speichert nur das Wort. Bildzeichen und deutscher Name
 *   stehen hier. Die Liste muss deckungsgleich mit
 *   MITARBEITER_SYMBOLPOOL in api/lib/auth.php sein — die Testsuite prüft
 *   das, sonst stünde irgendwann ein Bezeichner in der Datenbank, zu dem
 *   es kein Bild gibt.
 *
 * WARUM DIESE ACHT
 *   Motive der stillen Buchkultur, alle auf sechs Millimetern noch
 *   auseinanderzuhalten und in einem Wort zu benennen. Bewusst nicht zwei
 *   aus demselben Umfeld — ein Anker neben dem Segelboot wäre auf
 *   Chipgröße geraten statt erkannt.
 */
export type NutzerSymbol = {
  code: string;
  name: string;
  Icon: LucideIcon;
};

export const NUTZER_SYMBOLE: NutzerSymbol[] = [
  { code: 'feder', name: 'Feder', Icon: Feather },
  { code: 'lesezeichen', name: 'Lesezeichen', Icon: Bookmark },
  { code: 'vogel', name: 'Vogel', Icon: Bird },
  { code: 'tasse', name: 'Tasse', Icon: Coffee },
  { code: 'schluessel', name: 'Schlüssel', Icon: Key },
  { code: 'segelboot', name: 'Segelboot', Icon: Sailboat },
  { code: 'mond', name: 'Mond', Icon: Moon },
  { code: 'laterne', name: 'Laterne', Icon: Lamp }
];

/** Das Symbol zu einem Bezeichner, oder null. */
export function nutzerSymbol(code: string | null | undefined): NutzerSymbol | null {
  if (code === null || code === undefined || code === '') return null;
  return NUTZER_SYMBOLE.find((s) => s.code === code) ?? null;
}

/**
 * Wie der Chip vorgelesen wird.
 *
 * Der Name des Symbols steht VOR dem des Mitarbeiters, weil er die
 * Unterscheidung trägt: Wer die Zeile hört statt sieht, braucht zuerst
 * das Zeichen, das ihm fremd vorkommt.
 */
export function nutzerSymbolTitel(code: string | null | undefined, name: string): string {
  const s = nutzerSymbol(code);
  return s === null ? name : `${s.name} · ${name}`;
}
